/**
 * Error handling utility functions
 */

/**
 * Extract error message from various error types
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  if (error && typeof error === 'object') {
    const nested = error as { error?: { message?: string }; message?: string };
    if (typeof nested.error?.message === 'string' && nested.error.message) {
      return nested.error.message;
    }
    if ('message' in error) {
      return String((error as { message: unknown }).message);
    }
  }

  return 'An unknown error occurred';
}

/**
 * Extract error code from error object
 */
export function getErrorCode(error: unknown): string | null {
  if (error && typeof error === 'object') {
    if ('code' in error) {
      return String(error.code);
    }
    if ('errorCode' in error) {
      return String(error.errorCode);
    }
  }
  
  return null;
}

/**
 * Check if error is a network error
 */
export function isNetworkError(error: unknown): boolean {
  const message = getErrorMessage(error).toLowerCase();
  return (
    message.includes('network') ||
    message.includes('fetch') ||
    message.includes('connection') ||
    message.includes('timeout')
  );
}

/**
 * Check if error is a validation error
 */
export function isValidationError(error: unknown): boolean {
  const message = getErrorMessage(error).toLowerCase();
  return (
    message.includes('validation') ||
    message.includes('invalid') ||
    message.includes('required') ||
    message.includes('format')
  );
}

/**
 * Check if error is an authentication error
 */
export function isAuthError(error: unknown): boolean {
  const message = getErrorMessage(error).toLowerCase();
  const code = getErrorCode(error);
  
  return (
    message.includes('unauthorized') ||
    message.includes('authentication') ||
    message.includes('login') ||
    code === '401' ||
    code === 'UNAUTHORIZED'
  );
}

/**
 * Known Chrome/extension noise when content scripts mishandle chrome.runtime messaging
 * (common while viewing PDF iframes, forms, etc.). Not from application code.
 */
export function isBrowserExtensionPromiseRejection(reason: unknown): boolean {
  const message = getErrorMessage(reason).toLowerCase();
  return (
    message.includes('listener indicated an asynchronous response') ||
    message.includes('message channel closed before a response was received') ||
    message.includes('extension context invalidated') ||
    message.includes('receiving end does not exist')
  );
}

let browserExtensionRejectionFilterInstalled = false;

/**
 * Prevents known extension-only unhandled promise rejections from cluttering the console.
 * Call once during app bootstrap (web-portal / web-customer).
 */
export function suppressKnownBrowserExtensionPromiseRejections(): void {
  if (browserExtensionRejectionFilterInstalled || typeof window === 'undefined') {
    return;
  }
  browserExtensionRejectionFilterInstalled = true;

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    if (isBrowserExtensionPromiseRejection(event.reason)) {
      event.preventDefault();
    }
  });
}

/**
 * Check if error is a permission error
 */
export function isPermissionError(error: unknown): boolean {
  const message = getErrorMessage(error).toLowerCase();
  const code = getErrorCode(error);
  
  return (
    message.includes('forbidden') ||
    message.includes('permission') ||
    message.includes('access denied') ||
    code === '403' ||
    code === 'FORBIDDEN'
  );
}

/**
 * Format error for user display
 */
export function formatErrorForUser(error: unknown): string {
  const message = getErrorMessage(error);
  
  // Network errors
  if (isNetworkError(error)) {
    return 'Unable to connect to the server. Please check your internet connection and try again.';
  }
  
  // Authentication errors
  if (isAuthError(error)) {
    return 'Your session has expired. Please log in again.';
  }
  
  // Permission errors
  if (isPermissionError(error)) {
    return 'You do not have permission to perform this action.';
  }
  
  // Validation errors - return as-is (usually user-friendly)
  if (isValidationError(error)) {
    return message;
  }
  
  // Generic errors - sanitize for production
  if (message.includes('Internal Server Error') || message.includes('500')) {
    return 'An unexpected error occurred. Please try again later.';
  }
  
  // Return message if it seems user-friendly, otherwise generic message
  if (message.length < 100 && !message.includes('Error Code')) {
    return message;
  }
  
  return 'An error occurred. Please try again.';
}

/**
 * Log error (can be extended to integrate with logging service)
 */
export function logError(error: unknown, context?: string): void {
  const message = getErrorMessage(error);
  const code = getErrorCode(error);
  
  const logData = {
    message,
    code,
    context,
    timestamp: new Date().toISOString(),
    error: error instanceof Error ? {
      name: error.name,
      stack: error.stack,
    } : undefined,
  };
  
  // In production, this would send to a logging service
  console.error('Error logged:', logData);
}

/**
 * Create user-friendly error object
 */
export function createUserError(message: string, code?: string): { message: string; code?: string } {
  return {
    message,
    code,
  };
}

